import type { Pinia } from 'pinia';
import { SimpleObserver } from '@common/libs/observer';
import { useTokenStore } from '@common/stores/TokenStore';

export interface CallerOptionsCustom {
	/** Ajoute l'en-tête `Authorization` et gère le rafraîchissement du jeton. */
	useAuth?: boolean;
	/** N'émet pas la notification d'erreur globale — l'appelant s'en charge. */
	silentError?: boolean;
	/** Annule l'appel précédent portant la même clé (recherche à la frappe). */
	abortKey?: string;
	/**
	 * En-têtes recalculés à **chaque** appel.
	 *
	 * `headers` est figé à la construction du client ; or ce qui identifie
	 * l'appareil peut apparaître, changer ou disparaître en cours de session —
	 * une borne provisionnée, puis sortie du mode borne. Une fonction est le seul
	 * moyen de refléter cet état à l'instant de la requête.
	 */
	dynamicHeaders?: () => Record<string, string>;
	signal?: AbortSignal;
}

export type CallerOptions = CallerOptionsCustom & RequestInit;

export class AbortCallerException extends Error {
	constructor() {
		super('Appel annulé');
	}
}

/**
 * Erreur d'API structurée.
 *
 * Porte le `code` métier renvoyé par l'API (`RIGHT_DENIED`, `RIDE_ALREADY_TAKEN`…),
 * ce qui permet au front de réagir précisément sans analyser un message affichable.
 */
export class ApiException extends Error {
	constructor(
		readonly status: number,
		readonly code: string,
		message: string,
		readonly payload: Record<string, unknown> = {},
	) {
		super(message);
	}

	get isForbidden(): boolean {
		return this.status === 403;
	}

	get isUnauthorized(): boolean {
		return this.status === 401;
	}
}

/**
 * Client HTTP de l'application.
 *
 * Responsabilités : porter le jeton, rafraîchir de façon transparente quand il
 * expire, normaliser les erreurs, et exposer des observateurs pour brancher le
 * loader global et les notifications sans coupler les vues au réseau.
 */
export class Caller {
	readonly startObserver = new SimpleObserver();
	readonly beforeObserver = new SimpleObserver();
	readonly successObserver = new SimpleObserver();
	readonly errorObserver = new SimpleObserver();
	readonly completeObserver = new SimpleObserver();

	private abortControllers: Record<string, AbortController> = {};
	/** Un seul rafraîchissement à la fois : les appels concurrents attendent le même. */
	private refreshing: Promise<boolean> | null = null;

	constructor(
		private readonly baseUrl: string,
		private readonly pinia: Pinia,
		private readonly defaults: CallerOptions = {},
	) {}

	get<T = any>(url: string, options: CallerOptions = {}): Promise<T> {
		return this.call<T>('GET', url, undefined, options);
	}

	post<T = any>(url: string, body?: unknown, options: CallerOptions = {}): Promise<T> {
		return this.call<T>('POST', url, body, options);
	}

	patch<T = any>(url: string, body?: unknown, options: CallerOptions = {}): Promise<T> {
		return this.call<T>('PATCH', url, body, options);
	}

	put<T = any>(url: string, body?: unknown, options: CallerOptions = {}): Promise<T> {
		return this.call<T>('PUT', url, body, options);
	}

	delete<T = any>(url: string, options: CallerOptions = {}): Promise<T> {
		return this.call<T>('DELETE', url, undefined, options);
	}

	private async call<T>(
		method: string,
		url: string,
		body: unknown,
		options: CallerOptions,
		isRetry = false,
	): Promise<T> {
		const merged: CallerOptions = { ...this.defaults, ...options, method };
		const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;

		if (merged.abortKey) {
			this.abortControllers[merged.abortKey]?.abort();
			const controller = new AbortController();
			this.abortControllers[merged.abortKey] = controller;
			merged.signal = controller.signal;
		}

		const headers: Record<string, string> = {
			Accept: 'application/json',
			...(merged.dynamicHeaders?.() ?? {}),
			...((merged.headers as Record<string, string>) ?? {}),
		};

		if (body !== undefined && !(body instanceof FormData)) {
			headers['Content-Type'] = 'application/json';
			merged.body = JSON.stringify(body);
		} else if (body instanceof FormData) {
			merged.body = body;
		}

		if (merged.useAuth) {
			const token = useTokenStore(this.pinia).token;
			if (token?.accessToken) {
				headers.Authorization = `Bearer ${token.accessToken}`;
			}
		}
		merged.headers = headers;

		this.startObserver.trigger({ url: fullUrl, options: merged });
		this.beforeObserver.trigger({ url: fullUrl, options: merged });

		let response: Response;
		try {
			response = await fetch(fullUrl, merged as RequestInit);
		} catch (error) {
			this.completeObserver.trigger({ url: fullUrl });
			if ((error as Error).name === 'AbortError') {
				throw new AbortCallerException();
			}
			const exception = new ApiException(0, 'NETWORK_ERROR', 'Connexion indisponible.');
			if (!merged.silentError) {
				this.errorObserver.trigger(exception);
			}
			throw exception;
		}

		// Jeton expiré : on rafraîchit une seule fois puis on rejoue l'appel.
		if (response.status === 401 && merged.useAuth && !isRetry) {
			if (await this.refreshToken()) {
				return this.call<T>(method, url, body, options, true);
			}
		}

		this.completeObserver.trigger({ url: fullUrl });

		const payload = await this.parseBody(response);

		if (!response.ok) {
			const data = (payload ?? {}) as Record<string, any>;
			const nested = typeof data.message === 'object' ? data.message : data;
			const exception = new ApiException(
				response.status,
				nested?.code ?? data?.code ?? 'UNKNOWN_ERROR',
				nested?.message ?? data?.message ?? 'Une erreur est survenue.',
				nested ?? {},
			);
			if (!merged.silentError) {
				this.errorObserver.trigger(exception);
			}
			throw exception;
		}

		this.successObserver.trigger({ url: fullUrl, options: merged, result: payload });
		return payload as T;
	}

	private async parseBody(response: Response): Promise<unknown> {
		if (response.status === 204) {
			return null;
		}
		const text = await response.text();
		if (!text) {
			return null;
		}
		try {
			return JSON.parse(text);
		} catch {
			return text;
		}
	}

	/** Déclenche au plus un rafraîchissement, partagé par tous les appels en attente. */
	private refreshToken(): Promise<boolean> {
		this.refreshing ??= useTokenStore(this.pinia)
			.refresh()
			.finally(() => {
				this.refreshing = null;
			});
		return this.refreshing;
	}
}
